import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { aiFeatures } from "@/mocks/home";

export default function AISolutionsSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.08 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="solutions"
      ref={sectionRef}
      className="py-24 md:py-32 bg-background-100"
    >
      <div className="px-6 lg:px-12 xl:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left: Header + description */}
          <div className="reveal">
            <span className="text-xs font-semibold tracking-[0.2em] text-accent-600 uppercase mb-4 block">
              Artificial Intelligence
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 tracking-tight">
              {t("ai.title")}
            </h2>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-8 max-w-lg">
              {t("ai.subtitle")}
            </p>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-background-100 bg-accent-100 flex items-center justify-center"
                  >
                    <span className="text-[10px] font-bold text-accent-700">{i}</span>
                  </div>
                ))}
              </div>
              <span className="text-foreground-500 font-medium">
                4 AI capabilities, one platform
              </span>
            </div>
          </div>

          {/* Right: Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {aiFeatures.map((feature, index) => (
              <div
                key={feature.id}
                className="reveal group bg-white border border-foreground-100/60 rounded-xl p-6 hover:border-accent-200 transition-all duration-300"
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-4 group-hover:bg-accent-500 transition-colors duration-300">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${feature.icon} text-accent-600 group-hover:text-white text-lg transition-colors duration-300`} />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mb-2 tracking-tight">
                  {feature.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}